/**
 * `#/login` — la puerta. Etapa `R2-UI5`.
 *
 * PRD_R2 §«La pantalla de login» (tabla de elementos y tabla de errores) ·
 * `R2-F6-CA-03..10`, `-20`.
 *
 * ⛔ **Sin marco.** No lleva rail, ni sello, ni «Cambiar de brazo», ni
 * «Cerrar sesión»: quien está acá todavía no tiene sesión, y reusar
 * `MarcoTransversal` «para que se vea igual» le pone un menú con renglones a
 * alguien que no debería ver ninguno (orden 0, PRD:2202).
 *
 * ⛔ **Parametrizada.** La raíz de `auth.<sitio>` y el destino entran por props:
 * esta etapa no importa `App.tsx`, `rutas.ts` ni `configuracion.ts` — es `R2-UI6`
 * quien cablea, y es la única wave exclusiva de la SPA.
 */

import { useState } from "react"
import type { JSX } from "react"
import { crearBordeSesion, type BordeSesion } from "./borde-sesion.ts"
import { instancia } from "../zonas.ts"

/** Dirección a la que se manda a quien todavía no eligió contraseña (Regla 6). */
const DESTINO_ELEGIR_CONTRASENA = "#/elegir-contrasena"

/**
 * `OP-FIX-010` · lo que se pinta cuando el pedido ni siquiera llega: identity
 * caído, un corte, un CORS. Es OTRO texto que el del rechazo a propósito: con el
 * mismo, un servidor caído se leería como «me equivoqué de contraseña».
 */
export const TEXTO_SIN_CONEXION = "No pudimos conectar con el servidor. Prueba de nuevo en unos minutos."

export interface PropsPantallaLogin {
  /** Raíz de `auth.<sitio>`. La pone el marco; acá no se lee ninguna configuración. */
  readonly raiz: string
  /**
   * Dónde cae cada rol. **Esta etapa no lo decide** (`r2-ui5.md`): lo resuelve
   * `R2-UI6`, que es quien conoce las direcciones de brazo.
   */
  readonly destinoPorRol?: (rol: string) => string
  /** Cómo se cambia de dirección. También del marco. */
  readonly navegar?: (destino: string) => void
  /** Sólo para el examen de componente: un borde ya armado. */
  readonly borde?: BordeSesion
}

export function PantallaLogin({ raiz, destinoPorRol, navegar, borde }: PropsPantallaLogin): JSX.Element {
  const cable = borde ?? crearBordeSesion(raiz)

  const [correo, setCorreo] = useState("")
  const [clave, setClave] = useState("")
  const [errorCorreo, setErrorCorreo] = useState<string | null>(null)
  const [errorClave, setErrorClave] = useState<string | null>(null)
  const [rechazo, setRechazo] = useState<string | null>(null)
  const [enVuelo, setEnVuelo] = useState(false)

  const enviar = async (): Promise<void> => {
    if (enVuelo) return

    /*
      Los vacíos se señalan **cada uno** (`R2-F6-CA-06`), y no con un mensaje
      único arriba: la tabla de errores del PRD le da a cada campo su renglón.
    */
    const faltaCorreo = correo === ""
    const faltaClave = clave === ""
    setErrorCorreo(faltaCorreo ? "Escribe tu correo." : null)
    setErrorClave(faltaClave ? "Escribe tu contraseña." : null)
    if (faltaCorreo || faltaClave) {
      setRechazo(null)
      return
    }

    /*
      ⛔ `clave` viaja TAL CUAL. Sin `trim`, sin `replace`, sin `normalize`.
      RT-5 y Regla 3: la comparación es exacta contra lo que se cargó. Quitarle
      los puntos al DNI «para ayudar» hace `R2-F6-CA-10` imposible de cumplir, y
      lo que previene ese error es el texto de ayuda, no un arreglo silencioso.
    */
    setEnVuelo(true)
    setRechazo(null)
    try {
      const resultado = await cable.entrar(correo, clave)
      if (!resultado.entro) {
        /*
          ⛔ El texto es el que mandó el servidor —`SIGN_IN_REJECTED_COPY`— y es
          EL MISMO para los tres rechazos de la Regla 5: correo desconocido,
          contraseña incorrecta y cuenta dada de baja. Distinguirlos con un texto
          «más útil» le cuenta a alguien de afuera qué correos existen.
        */
        setRechazo(resultado.mensaje)
        return
      }

      /*
        Entró. Quién es y adónde va lo decide el MARCO: acá sólo se pregunta si
        tiene la marca puesta (Regla 6) y se llama a la prop.
      */
      let debeElegir = false
      let rol = ""
      try {
        const actor = await cable.quienSoy()
        debeElegir = actor?.mustChoosePassword === true
        rol = actor?.role ?? ""
      } catch {
        /* si «quién soy» se cayó, el marco lo resolverá al montar */
      }
      const destino = debeElegir
        ? DESTINO_ELEGIR_CONTRASENA
        : (destinoPorRol?.(rol) ?? DESTINO_ELEGIR_CONTRASENA)
      navegar?.(destino)
    } catch {
      /*
        ⟦rastro · OP-FIX-010 · OP-H-010⟧ Acá había un `try … finally` sin `catch`:
        el borde LANZA cuando el pedido no llega (`borde-sesion.ts`, `pedir`, con
        `statusCode: 0`) y el error no llegaba a ningún estado — «Entrar» volvía
        a su lugar y la pantalla no decía nada. Lo único que lanza `entrar` es
        eso: un rechazo del servidor vuelve como `{ entro: false }`, arriba.
      */
      setRechazo(TEXTO_SIN_CONEXION)
    } finally {
      setEnVuelo(false)
    }
  }

  return (
    <main className="login">
      {/*
        ⛔ EL NOMBRE SALE DE LA DECLARACIÓN, NUNCA DE UN LITERAL — `[23/9/2026]`.
        ~~Acá decía `Entrar a KAI`.~~ Se REDACTA y no se borra (`ADR-0082`).

        Medido en el UAT: con la consola de `OLI` servida desde
        `oli.bidchile-uat.duckdns.org`, esta pantalla decía **«Entrar a KAI»** —el
        nombre de OTRA instancia, en la primera pantalla que ve una persona, antes
        de escribir su contraseña—. `PRD_R1.md:194` es la regla general y este
        archivo era el único de la consola que no la seguía: un `grep` de
        `instancia()` en `src/login/**` daba CERO.

        ⛔ Y si la instancia NO declara su nombre, dice «Entrar» a secas: un
        default inventado acá es exactamente lo que produjo el defecto.
      */}
      <h1 className="login__titulo">
        {instancia() === null ? "Entrar" : `Entrar a ${instancia()}`}
      </h1>

      {/*
        ⛔ ARRIBA del formulario, que es lo que dice la columna «Dónde» del PRD.
        Abajo del botón se lee después de haber vuelto a mirar el formulario
        entero, y en una pantalla de dos campos eso es toda la pantalla.
      */}
      {rechazo === null ? null : (
        <p className="login__rechazo" role="alert">
          {rechazo}
        </p>
      )}

      <form
        className="login__formulario"
        onSubmit={(evento) => {
          evento.preventDefault()
          void enviar()
        }}
      >
        <div className="login__campo">
          <label htmlFor="login-correo">Correo</label>
          <input
            id="login-correo"
            type="text"
            value={correo}
            autoComplete="username"
            aria-invalid={errorCorreo !== null}
            onChange={(evento) => {
              setCorreo(evento.target.value)
            }}
          />
          {errorCorreo === null ? null : (
            <p className="login__error-de-campo" role="alert">
              {errorCorreo}
            </p>
          )}
        </div>

        <div className="login__campo">
          <label htmlFor="login-contrasena">Contraseña</label>
          <input
            id="login-contrasena"
            type="password"
            value={clave}
            autoComplete="current-password"
            aria-invalid={errorClave !== null}
            onChange={(evento) => {
              setClave(evento.target.value)
            }}
          />
          {errorClave === null ? null : (
            <p className="login__error-de-campo" role="alert">
              {errorClave}
            </p>
          )}
          {/*
            ⛔ DEBAJO del campo «Contraseña», textual del PRD. Arriba del campo se
            lee antes de saber de qué se está hablando, y es el único texto que
            evita que alguien tipee el DNI con puntos (`R2-F6-CA-10`).
          */}
          <p className="login__ayuda">Si es tu primera vez, tu contraseña es tu DNI, sin puntos ni guiones.</p>
        </div>

        {/*
          El freno VISIBLE: mientras el pedido está en vuelo el botón no deja
          mandar otro. ⛔ Y no se cuenta ningún intento ni se habla de bloqueo: el
          freno es del servidor (Regla 25) y **la cuenta nunca se bloquea**.
        */}
        {/*
          ⛔ La acción cuelga del `onClick` del botón y no sólo del `onSubmit` del
          formulario: un `click` despachado sobre el botón —que es lo que hace un
          examen de componente, y lo que hace un lector de pantalla— no siempre
          produce el `submit`. El `preventDefault()` evita que además se envíe el
          formulario, así que `enviar()` corre UNA vez.
        */}
        <button
          className="boton boton--primario login__entrar"
          type="submit"
          disabled={enVuelo}
          onClick={(evento) => {
            evento.preventDefault()
            void enviar()
          }}
        >
          Entrar
        </button>
      </form>

      <p className="login__pie">¿Olvidaste tu contraseña? Pídesela al equipo técnico de Patagonian.</p>
    </main>
  )
}
